import { Field, useFormikContext } from 'formik';
import type { FieldInputProps, FieldMetaProps, FormikState } from 'formik';
import { forwardRef, useCallback } from 'react';
import type { ChangeEvent, FocusEvent } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { TextInput } from '@hivebrite/design-system/Inputs';

import { cleanValue } from 'hb-react/shared/components/formik/components/inputs/utils';
import { shouldDisplayError } from 'hb-react/shared/components/formik/utils/shouldDisplayError';

import type { TextProps } from './types';
import { validateText } from './validate';

const Text = forwardRef<HTMLInputElement, TextProps<HTMLInputElement>>(
  (
    {
      ariaDescribedBy,
      ariaLabel,
      autoComplete,
      disabled = false,
      icon,
      id,
      clearable = true,
      inputProps = {},
      info,
      infoIcon,
      label,
      labelMultiline,
      name,
      trimOnBlur = false,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      tooltip,
      validate: {
        errorMessage,
        maxEmailsInline,
        maxLength,
        minLength,
        regex,
        required,
        urlFormat,
        urlFormatWithProtocol,
        urlAndMailToFormat,
        validEmailsInline,
        excludeRegex,
      } = {},
      wrapperProps = {},
    },
    ref,
  ) => {
    const formik = useFormikContext();
    const htmlId = id || name;

    const handleBlur = useCallback(
      (e: FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(e);

        if (trimOnBlur) {
          formik.setFieldValue(name, cleanValue(e.target.value, trimOnBlur));
        }

        onBlur?.(e);
      },
      [formik, onBlur, trimOnBlur, name],
    );

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(e);
        onChange?.(e.target.value);
      },
      [formik, onChange],
    );

    const handleValidate = useCallback(
      (value: string) =>
        validateText({
          errorMessage,
          maxEmailsInline,
          maxLength,
          minLength,
          regex,
          required,
          urlFormat,
          urlFormatWithProtocol,
          urlAndMailToFormat,
          validEmailsInline,
          value,
          excludeRegex,
        }),
      [
        errorMessage,
        maxEmailsInline,
        maxLength,
        minLength,
        regex,
        required,
        urlFormat,
        urlFormatWithProtocol,
        urlAndMailToFormat,
        validEmailsInline,
        excludeRegex,
      ],
    );

    return (
      <Box mb={6} {...wrapperProps}>
        <Field name={name} validate={handleValidate}>
          {({
            field,
            form,
            meta,
          }: {
            field: FieldInputProps<any>;
            form: FormikState<any>;
            meta: FieldMetaProps<any>;
          }) => (
            <TextInput
              {...field}
              ariaDescribedBy={ariaDescribedBy}
              ariaLabel={ariaLabel}
              autoComplete={autoComplete}
              clearable={clearable}
              dataTestId="formik-text"
              disabled={disabled}
              error={shouldDisplayError({ meta, form })}
              icon={icon}
              id={htmlId}
              info={info}
              infoIcon={infoIcon}
              inputProps={inputProps}
              label={label}
              labelMultiline={labelMultiline}
              onBlur={handleBlur}
              onChange={handleChange}
              onFocus={onFocus}
              placeholder={placeholder}
              ref={ref}
              required={required}
              tooltip={tooltip}
            />
          )}
        </Field>
      </Box>
    );
  },
);

/* eslint-disable-next-line import/no-unused-modules */
export default Text;
